import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon } from '@agconnections/grow-ui';
import { CloseRounded } from '@material-ui/icons';
import SuccessIcon from 'components/Icons/components/SuccessIcon';
import WarningIcon from 'components/Icons/components/WarningIcon';
import ErrorIcon from 'assets/error-circle-outline.svg?react';

import './index.css';

const toastDefaultPropOnClose = () => {};
const toastDefaultPropCustomStyle = {};

// The design's notification closes with an 18px glyph; the icon's own
// default renders 24px.
const CLOSE_ICON_SIZE = 18;

// The design's 14px/20px body token carries this tracking. It is load-bearing
// here, not cosmetic: without it the first sentence of a two-line message
// overflows its line and breaks a word early.
const BODY_TRACKING = '-0.105px';
const Toast = ({
  type = 'success',
  title = '',
  onClose = toastDefaultPropOnClose,
  open = false,
  timeout = 5000,
  children = null,
  isBold = false,
  customStyle = toastDefaultPropCustomStyle
}) => {
  const [showToast, setShowToast] = useState(open);

  useEffect(() => {
    setShowToast(open);
  }, [open]);

  useEffect(() => {
    let timer;
    if (showToast && timeout >= 0) {
      timer = setTimeout(() => {
        setShowToast(false);
        onClose();
      }, timeout);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [showToast, timeout, onClose]);

  const ToastIcon = {
    success: <SuccessIcon width={24} height={24} />,
    warning: <WarningIcon />,
    info: <Icon icon="error" color="#0092E4" size={24} />,
    error: <ErrorIcon />
  };

  const borderColor = {
    success: '#19A04B',
    warning: '#ECC94B',
    info: '#0092E4',
    error: '#CF3537'
  };

  return (
    <div
      className={` toast fixed transition-all duration-500 ease-in top-9 ${showToast &&
        'show-toast'} py-4 px-6 bg-white rounded flex ${
        children ? 'w-2/6' : 'w-1/4'
      }`}
      style={{
        zIndex: 2000,
        // Drawn as an inset shadow, the way the design does, so the status bar
        // sits over the padding instead of taking 4px out of the content box.
        boxShadow: `inset 4px 0 0 -1px ${borderColor[type]}, 0 2px 3px 3px rgba(0, 0, 0, 0.2)`,
        ...customStyle
      }}
      data-testid="toast-alert"
    >
      <div className="w-full flex">
        <div className="mr-4">{ToastIcon[type]}</div>
        <div className="w-full">
          <div className="flex justify-between font-body items-center">
            <h4
              className={`text-base capitalize-first ${
                isBold ? 'font-semibold' : ''
              }`}
            >
              {title}
            </h4>
            <Button
              ariaLabel="alert-close"
              type="text"
              onClick={onClose}
              icon={<CloseRounded style={{ fontSize: CLOSE_ICON_SIZE }} />}
              size="small"
            />
          </div>
          {children && (
            <div
              className="mt-2 text-sm leading-5"
              style={{ letterSpacing: BODY_TRACKING }}
            >
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

Toast.propTypes = {
  type: PropTypes.oneOf(['success', 'warning', 'error', 'info']),
  onClose: PropTypes.func,
  title: PropTypes.string,
  children: PropTypes.node,
  open: PropTypes.bool,
  timeout: PropTypes.number,
  isBold: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  customStyle: PropTypes.object
};

export default Toast;
